// src/constants/geo.ts
/* geo.ts — SSOT: Regions/Cities/Types + canonical path builders (2026, no duplicates) */

export const REGIONS = ['דרום', 'מרכז', 'צפון'] as const;
export type Region = (typeof REGIONS)[number];

export const REGION_CITIES: Readonly<Record<Region, readonly string[]>> = {
  דרום: [
    'באר שבע',
    'אשקלון',
    'אשדוד',
    'קריית גת',
    'אופקים',
    'נתיבות',
    'שדרות',
    'להבים',
    'עומר',
    'דימונה',
    'קריית מלאכי',
    'ערד',
    'ירוחם',
    'מיתר',
    'אילת',
    'קריית עקרון',
    'גן יבנה',
  ],
  מרכז: [
    'תל אביב',
    'רמת גן',
    'גבעתיים',
    'רמת השרון',
    'אור יהודה',
    'ראשון לציון',
    'חולון',
    'בת ים',
    'רעננה',
    'הרצליה',
    'פתח תקווה',
    'יבנה',
    'רחובות',
    'גדרה',
    'נס ציונה',
    'כפר סבא',
    'הוד השרון',
    'מודיעין',
    'ירושלים',
    'בית שמש',
    'מעלה אדומים',
    'רמלה',
    'ראש העין',
    'נתניה',
    'חדרה',
    'עמק חפר',
  ],
  צפון: [
    'חיפה',
    'קריות',
    'יקנעם',
    'עפולה',
    'עמק יזרעאל',
    'נוף הגליל',
    'עכו',
    'נהריה',
    'כרמיאל',
    'טבריה',
    'קיסריה',
    'זכרון יעקב',
  ],
} as const;

export const SERVICE_FAMILY = 'תקליטן' as const;

export const EVENT_TYPES = ['חתונה', 'חינה', 'בר-מצווה', 'אירוע'] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/** SeoCity – כל הערים + '' (כי גלובלי משתמש בזה לפעמים) */
export const SEO_ALL_CITIES = [
  ...REGION_CITIES.דרום,
  ...REGION_CITIES.מרכז,
  ...REGION_CITIES.צפון,
] as const;
export type SeoCity = (typeof SEO_ALL_CITIES)[number] | '';

/* ---- normalization helpers ---- */
const stripBidi = (s: string) => {
  try {
    return s.normalize('NFKC').replace(/\u200E|\u200F/g, '');
  } catch {
    return s.replace(/\u200E|\u200F/g, '');
  }
};
const collapseSpaces = (s: string) => s.replace(/\s+/g, ' ').trim();
const toHyphen = (s: string) => s.replace(/\s+/g, '-');

const normKey = (s: string) =>
  collapseSpaces(stripBidi(String(s)))
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .toLowerCase();

/* ---- slug helpers (אחיד) ---- */
export const cityToSlug = (city: string) => encodeURI(toHyphen(collapseSpaces(stripBidi(city))));
export const regionToSlug = (r: Region) => encodeURI(collapseSpaces(stripBidi(r)));
export const typeToSlug = (t: EventType) => encodeURI(collapseSpaces(stripBidi(t)));

export const slugToCityName = (slug: string) =>
  collapseSpaces(stripBidi(decodeURIComponent(slug))).replace(/-/g, ' ');

export const slugToRegionName = (slug: string) => collapseSpaces(stripBidi(decodeURIComponent(slug)));

export function slugToTypeName(slug: string): EventType | null {
  const raw = (() => {
    try {
      return decodeURIComponent(String(slug));
    } catch {
      return String(slug);
    }
  })();

  const t = collapseSpaces(stripBidi(raw));
  return (EVENT_TYPES as readonly string[]).includes(t) ? (t as EventType) : null;
}

export function isRegionName(x: string): x is Region {
  return (REGIONS as readonly string[]).includes(collapseSpaces(stripBidi(x)) as any);
}

export function findRegionByCity(city: string): Region | null {
  const c = collapseSpaces(stripBidi(city));
  for (const r of REGIONS) {
    if ((REGION_CITIES[r] || []).includes(c)) return r;
  }
  return null;
}

/* ---- City lookup maps ---- */
export const ALL_CITIES = [...SEO_ALL_CITIES] as readonly SeoCity[];

const CITY_BY_KEY = new Map<string, SeoCity>();
for (const c of ALL_CITIES) {
  const cityName = String(c);
  const slug = decodeURIComponent(cityToSlug(cityName)); // לדוגמה "באר-שבע"
  CITY_BY_KEY.set(normKey(cityName), c);
  CITY_BY_KEY.set(normKey(slug), c); // "באר שבע" אחרי normKey
}

export function resolveCityFromSlugMaybe(slugOrName: string): SeoCity | null {
  const raw = (() => {
    try {
      return decodeURIComponent(String(slugOrName));
    } catch {
      return String(slugOrName);
    }
  })();
  return CITY_BY_KEY.get(normKey(raw)) ?? null;
}

/* ---- canonical paths (only these!) ---- */
const ensureTrailingSlash = (p: string) => (p.endsWith('/') ? p : `${p}/`);

/** /תקליטן/<type>/ */
export function globalTypePath(type: EventType) {
  return ensureTrailingSlash(`/${SERVICE_FAMILY}/${typeToSlug(type)}`);
}

/** /תקליטן/<type>/<location>/  (location = עיר או אזור) */
const safeDecode = (s: string) => {
  try { return decodeURIComponent(s); } catch { return s; }
};

export function typeLocationPath(type: EventType, locationSlugOrName: string) {
  const raw = collapseSpaces(stripBidi(safeDecode(String(locationSlugOrName))));
  const asRegion = isRegionName(raw) ? (raw as Region) : null;

  const asCity = asRegion ? null : resolveCityFromSlugMaybe(raw);
  const locSlug = asRegion ? regionToSlug(asRegion) : cityToSlug(asCity ? String(asCity) : raw);

  return ensureTrailingSlash(`/${SERVICE_FAMILY}/${typeToSlug(type)}/${locSlug}`);
}

/** legacy term -> type */
export function guessTypeFromLegacyTerm(term: string): EventType {
  const t = stripBidi(String(term)).replace(/-/g, ' ').toLowerCase();
  if (t.includes('חתונה')) return 'חתונה';
  if (t.includes('חינה')) return 'חינה';
  if (t.includes('בר') && t.includes('מצ')) return 'בר-מצווה';
  return 'אירוע';
}
